// Pure logic for the merged WhatsApp thread view — gathering a patient's per-clinic WhatsApp
// threads and interleaving their histories into one clustered, tenant(number)-aware stream.
//
// Data model note: healo_msg has NO per-message tenantId. The clinic/number dimension of a
// WhatsApp thread is its `whatsappBotId` (one thread per (patientUserId, whatsappBotId) pair);
// `whatsappNumberLabel` is the display string for that number. Everything here keys off those.
//
// No React, no I/O — kept side-effect-free so it can be reasoned about (and unit-tested) on its
// own; the hook owns fetching each thread's history and feeds the ascending message arrays in.

import { appIsSameDay, withAppTimeZone } from "@/lib/timezone";
import {
  isCallLogMessage,
  isMetadataAutoReplyMessage,
  isStandaloneSystemLineMessage,
} from "@/lib/realtime/chat-message-helpers";
import { getSenderGroupKey, getThreadRowId } from "@/lib/messages/messages-helpers";

/** A thread row is a WhatsApp conversation. */
export function isWhatsappThreadRow(thread) {
  return String(thread?.metadata?.conversationType || "").toLowerCase() === "whatsapp";
}

/** The clinic/gateway number a WhatsApp thread is bound to (its "tenant" for the merged view). */
export function threadWhatsappBotId(thread) {
  return String(thread?.whatsappBotId || "").trim();
}

/**
 * Every WhatsApp thread that belongs to the same patient as `selectedThread`, including the
 * selected thread itself. Read-only: it matches on `patientUserId` across the candidate rows the
 * caller passes in — the windowed list + attention sections + search + the selected row, PLUS the
 * rows the hook fetches with `GET /api/threads?patientUserId=` so a clinic thread that is too old
 * or too quiet to be in the loaded list still merges. Threads with no patient link or no bound
 * number are skipped (they can't be attributed to a clinic). Deduped by threadId, first copy wins,
 * so callers should pass live in-memory rows ahead of any fetched snapshot.
 *
 * Returns [] when the selected thread is not a patient WhatsApp thread, so callers can treat an
 * empty/<=1 result as "not merged".
 */
export function collectPatientWhatsappThreadRows(selectedThread, candidateRows) {
  const patientUserId = String(selectedThread?.patientUserId || "").trim();
  if (
    !patientUserId ||
    !isWhatsappThreadRow(selectedThread) ||
    !threadWhatsappBotId(selectedThread)
  ) {
    return [];
  }
  const byThreadId = new Map();
  const consider = (t) => {
    if (!t) return;
    const id = getThreadRowId(t);
    if (!id || byThreadId.has(id)) return;
    if (String(t?.patientUserId || "").trim() !== patientUserId) return;
    if (!isWhatsappThreadRow(t) || !threadWhatsappBotId(t)) return;
    byThreadId.set(id, t);
  };
  // Selected thread first so it always wins as the canonical copy of its own row.
  consider(selectedThread);
  for (const t of candidateRows || []) consider(t);
  return Array.from(byThreadId.values());
}

/** Milliseconds for a message's createdAt, 0 when missing/unparseable (keeps sorts total). */
function messageTimeMs(message) {
  const t = new Date(message?.createdAt || 0).getTime();
  return Number.isFinite(t) ? t : 0;
}

/**
 * Interleave several threads' ascending message arrays into one time-ordered cluster list, the
 * same shape `clusterHistoryMessages` produces ({type:"auto"|"call"|"system", item} or
 * {type:"chat", items}) PLUS a `threadId`/`botId` on every cluster identifying its source clinic.
 *
 * A chat run only extends while BOTH the sender group key AND the source thread stay the same, so a
 * segment never spans two clinics even when the same person (or two clinics' bots) message back to
 * back — this is what lets the renderer draw one rail per clinic.
 *
 * @param {Array<{ threadId: string, botId: string, items: object[] }>} buckets
 */
export function buildMergedClusters(buckets) {
  const flat = [];
  (buckets || []).forEach((bucket, bucketIndex) => {
    const threadId = String(bucket?.threadId || "").trim();
    const botId = String(bucket?.botId || "").trim();
    (bucket?.items || []).forEach((msg, itemIndex) => {
      flat.push({ msg, threadId, botId, bucketIndex, itemIndex, t: messageTimeMs(msg) });
    });
  });
  // Chronological; ties broken deterministically (bucket, then original order) so equal timestamps
  // don't reshuffle between renders.
  flat.sort(
    (a, b) => a.t - b.t || a.bucketIndex - b.bucketIndex || a.itemIndex - b.itemIndex,
  );

  const clusters = [];
  let i = 0;
  while (i < flat.length) {
    const cur = flat[i];
    const m = cur.msg;
    const stamp = { threadId: cur.threadId, botId: cur.botId };
    if (isMetadataAutoReplyMessage(m)) {
      clusters.push({ type: "auto", item: m, ...stamp });
      i += 1;
      continue;
    }
    if (isCallLogMessage(m)) {
      clusters.push({ type: "call", item: m, ...stamp });
      i += 1;
      continue;
    }
    if (isStandaloneSystemLineMessage(m)) {
      clusters.push({ type: "system", item: m, ...stamp });
      i += 1;
      continue;
    }
    const key = getSenderGroupKey(m);
    if (!key) {
      clusters.push({ type: "chat", items: [m], ...stamp });
      i += 1;
      continue;
    }
    const group = [m];
    i += 1;
    while (i < flat.length) {
      const nxt = flat[i];
      if (nxt.threadId !== cur.threadId) break;
      if (isStandaloneSystemLineMessage(nxt.msg) || isCallLogMessage(nxt.msg)) break;
      if (getSenderGroupKey(nxt.msg) !== key) break;
      group.push(nxt.msg);
      i += 1;
    }
    clusters.push({ type: "chat", items: group, ...stamp });
  }
  return clusters;
}

/**
 * Display label for a thread's bound clinic number: the row's own `whatsappNumberLabel` when the
 * server enriched it, else the label from the settings `whatsappNumbers` list, else a neutral
 * fallback.
 */
export function numberLabelForThread(thread, whatsappNumbers) {
  const direct = String(thread?.whatsappNumberLabel || "").trim();
  if (direct) return direct;
  const botId = threadWhatsappBotId(thread);
  const match = (whatsappNumbers || []).find((n) => String(n?.botId || "").trim() === botId);
  return String(match?.label || "").trim() || "WhatsApp";
}

/**
 * A short clinic name from a number label for the rail chip / picker pill — strips a trailing phone
 * number (e.g. "Bedok Clinic +65 6448 2011" → "Bedok Clinic"). Falls back to the whole label when
 * it is only a phone number.
 */
export function deriveClinicShortName(label) {
  const s = String(label || "").trim();
  if (!s) return "WhatsApp";
  const withoutPhone = s.replace(/[·|,–-]?\s*\+?[\d][\d\s()\-.]{5,}$/u, "").trim();
  return withoutPhone || s;
}

/** Compact last-activity stamp for a source: today → time of day, otherwise the weekday. */
export function formatSourceLastActivity(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const isToday = appIsSameDay(date, new Date());
  return isToday
    ? date.toLocaleTimeString([], withAppTimeZone({ hour: "2-digit", minute: "2-digit" }))
    : date.toLocaleDateString([], withAppTimeZone({ weekday: "short" }));
}

/**
 * The "number · last activity" line shown under a clinic's short name in the reply-from picker and
 * the source filter. The number is the part of the label the short name doesn't already cover, so
 * "Bedok +65 6448 2011" reads as "Bedok" / "+65 6448 2011 · 09:12".
 */
export function sourceSubtitle(source) {
  const label = String(source?.label || "").trim();
  const shortName = String(source?.shortName || "").trim();
  const numberPart =
    label && label !== shortName
      ? label.replace(shortName, "").replace(/^[\s·|,–-]+/, "").trim()
      : "";
  return [numberPart, formatSourceLastActivity(source?.lastActivityAt)].filter(Boolean).join(" · ");
}

/**
 * The reply-from picker sources for a merged patient: one entry per sibling thread with its
 * threadId, botId, full number label, short clinic name and last-activity time. Sorted most-recent
 * first so the clinic the patient last used sits at the top (matching the design reference).
 */
export function buildMergedSources(siblingThreads, whatsappNumbers) {
  return (siblingThreads || [])
    .map((thread) => {
      const label = numberLabelForThread(thread, whatsappNumbers);
      return {
        threadId: getThreadRowId(thread),
        botId: threadWhatsappBotId(thread),
        label,
        shortName: deriveClinicShortName(label),
        lastActivityAt: thread?.lastMessageAt || null,
      };
    })
    .filter((s) => s.threadId && s.botId)
    .sort(
      (a, b) =>
        new Date(b.lastActivityAt || 0).getTime() - new Date(a.lastActivityAt || 0).getTime(),
    );
}

/**
 * The reply-from picker's full option list for a patient: every clinic number the patient already
 * has a thread with (from `buildMergedSources`, flagged `hasThread: true`), followed by every other
 * configured business number, flagged `hasThread: false` with an empty `threadId`.
 *
 * A threadless entry is a send target only — it never joins the rendered stream or the source
 * filter, because there is nothing to show yet. Picking one and sending creates that number's
 * thread first (find-or-create), which is what lets staff open a conversation from a clinic the
 * patient has never written to instead of being limited to the numbers already in use.
 *
 * The extras keep the order the settings endpoint returned (it already applies the configured
 * display order), so the picker lists them the same way the rest of the app does.
 */
export function buildReplyFromSources(threadSources, whatsappNumbers) {
  const existing = (threadSources || []).map((source) => ({ ...source, hasThread: true }));
  const used = new Set(existing.map((source) => source.botId));
  const extras = [];
  for (const number of whatsappNumbers || []) {
    const botId = String(number?.botId || "").trim();
    if (!botId || used.has(botId)) continue;
    used.add(botId);
    const label = String(number?.label || "").trim() || "WhatsApp";
    extras.push({
      threadId: "",
      botId,
      label,
      shortName: deriveClinicShortName(label),
      lastActivityAt: null,
      hasThread: false,
    });
  }
  return extras.length ? [...existing, ...extras] : existing;
}
